@layer components {
  .page {
    container-type: inline-size;
    display: grid;
    gap: var(--space-6);
  }

  .intro {
    display: grid;
    gap: var(--space-4);
    align-items: end;
    padding-inline: var(--space-2);
    color: var(--color-on-frame);

    @container (inline-size >= 48rem) {
      grid-template-columns: minmax(0, 1fr) auto;
    }
  }

  .heading {
    display: grid;
    gap: var(--space-3);
  }

  .title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    text-shadow: var(--text-shadow-frame);
  }

  .note {
    max-inline-size: 46ch;
    color: var(--color-on-frame-muted);
    font-size: var(--text-sm);
    font-weight: 520;
    text-wrap: pretty;
  }

  .scope {
    color: var(--color-on-frame);
    font-size: var(--text-sm);
    font-weight: 620;
  }

  .scopeLink {
    display: inline-block;
    padding-block: calc((var(--tap-target) - 1lh) / 2);
    margin-block: calc((1lh - var(--tap-target)) / 2);
    border-radius: var(--radius-md);
    color: inherit;
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    text-underline-offset: 0.2em;

    &:focus-visible {
      outline: 2.5px solid var(--color-on-frame);
      outline-offset: 2px;
    }
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);

    @container (inline-size < 26rem) {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .filter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-block-size: var(--tap-target);
    padding-inline: var(--space-4);
    border-radius: var(--radius-full);
    background: var(--color-frame-pill);
    color: var(--color-on-frame);
    font-size: var(--text-sm);
    font-weight: 620;
    text-decoration: none;
    white-space: nowrap;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    @media (hover: hover) {
      &:hover:not([aria-current]) {
        background: var(--color-frame-pill-hover);
      }
    }

    &:active {
      scale: 0.96;
      transition-duration: 60ms;
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-on-frame);
      outline-offset: 2px;
    }

    &[aria-current] {
      background: var(--color-surface);
      color: var(--color-text);
      box-shadow: var(--shadow-sm);
    }
  }

  .filtersSkeleton {
    inline-size: 22rem;
    max-inline-size: 100%;
    block-size: var(--tap-target);
    background-color: var(--color-frame-pill);
  }

  .sheet {
    padding: var(--space-5) var(--space-6) var(--space-6);

    @container (inline-size < 30rem) {
      padding: var(--space-4) var(--space-4) var(--space-5);
    }
  }

  .more {
    display: grid;
    justify-items: center;
  }

  .log {
    --log-columns: 4.5rem minmax(10rem, 0.9fr) minmax(0, 1.5fr) minmax(0, 1.1fr);
    --log-columns-compact: 4.5rem minmax(10rem, 22rem) minmax(0, 1fr);

    container-type: inline-size;
    display: grid;
    gap: var(--space-5);
  }

  .columns {
    display: none;

    @container (inline-size >= 56rem) {
      display: grid;
      grid-template-columns: var(--log-columns);
      gap: var(--space-4);
      color: var(--color-text-muted);
      font-size: var(--text-xs);
      font-weight: 620;
      white-space: nowrap;

      &[data-compact] {
        grid-template-columns: var(--log-columns-compact);
      }
    }
  }

  .day {
    display: grid;
    gap: var(--space-1);
  }

  .dayTitle {
    color: var(--color-text-muted);
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: 650;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  .rows {
    display: grid;
    margin: 0 calc(-1 * var(--space-2));
    padding: 0;
    list-style: none;
  }

  .row {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-1) var(--space-3);
    padding: var(--space-3) var(--space-2);

    & + &::before {
      content: "";
      position: absolute;
      inset-inline: var(--space-2);
      inset-block-start: 0;
      border-block-start: 1.5px solid var(--color-border);
    }

    @container (inline-size >= 56rem) {
      grid-template-columns: var(--log-columns);
      gap: var(--space-4);
      min-block-size: 3.25rem;
      padding-block: var(--space-1);
    }
  }

  .time {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 560;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .whatCell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1) var(--space-2);
    min-inline-size: 0;
  }

  .what {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-inline-size: 0;
    font-size: var(--text-sm);
    font-weight: 650;
    font-variant-numeric: tabular-nums;

    &[data-refused] {
      padding: 0.125rem 0.625rem 0.125rem 0.45rem;
      border-radius: var(--radius-full);
      background: var(--color-danger-soft);
      color: var(--color-danger);
    }

    & svg {
      flex: none;
      inline-size: 0.9375rem;
      block-size: 0.9375rem;
      stroke-width: 2.3;
    }
  }

  .item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-inline-size: 0;
    min-block-size: 1.5rem;
    font-size: var(--text-sm);
  }

  .itemLink,
  .actorLink {
    position: relative;
    padding-block: calc((var(--tap-target) - 1lh) / 2);
    margin-block: calc((1lh - var(--tap-target)) / 2);
    min-inline-size: 0;
    overflow: hidden;
    border-radius: var(--radius-md);
    color: var(--color-text);
    font-size: var(--text-sm);
    font-weight: 620;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (hover: hover) {
      &:hover {
        color: var(--color-accent);
        text-decoration: underline;
        text-decoration-thickness: 1.5px;
        text-underline-offset: 0.2em;
      }
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: 2px;
    }
  }

  .version {
    display: inline-flex;
    flex: none;
    align-items: center;
    min-block-size: 1.375rem;
    padding-inline: var(--space-2);
    border-radius: var(--radius-full);
    background: var(--candy-gloss), var(--sticker-other);
    color: var(--sticker-ink);
    font-size: var(--text-xs);
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    box-shadow: inset 0 -2px 3px -1px color-mix(in oklch, var(--sticker-other), black 22%);
  }

  .who {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0 var(--space-2);
    min-inline-size: 0;
    min-block-size: 1.5rem;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 560;

    & svg {
      flex: none;
      inline-size: 1rem;
      block-size: 1rem;
      stroke-width: 2.2;
    }
  }

  .byLabel {
    color: var(--color-text-faint);
    font-size: var(--text-xs);
    font-weight: 620;

    @container (inline-size >= 56rem) {
      position: absolute;
      inline-size: 1px;
      block-size: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
  }

  .owner {
    color: var(--color-text);
    font-weight: 620;
  }

  .person {
    min-inline-size: 0;
    overflow-wrap: anywhere;
  }

  .actorLink {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;

    & svg {
      color: var(--color-accent);
    }
  }

  .actorTag {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-variant-numeric: tabular-nums;
  }

  .actorState {
    color: var(--color-text-faint);
    font-size: var(--text-xs);
    font-weight: 600;
  }

  .quiet {
    color: var(--color-text-faint);
  }

  .row > .whatCell {
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
  }

  .row > .time {
    grid-column: 2;
    grid-row: 1;
  }

  .row > .item,
  .row > .who {
    grid-column: 1 / -1;
  }

  @container (inline-size >= 56rem) {
    .row > .time {
      grid-column: 1;
    }

    .row > .whatCell {
      grid-column: 2;
    }

    .row > .item {
      grid-column: 3;
    }

    .row > .who {
      grid-column: 4;
    }

    .row[data-compact] {
      grid-template-columns: var(--log-columns-compact);
    }

    .row[data-compact] > .who {
      grid-column: 3;
    }
  }

  .problem {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
    color: var(--color-text-muted);
    font-size: var(--text-sm);

    & > svg {
      flex: none;
      inline-size: 1.125rem;
      block-size: 1.125rem;
      color: var(--color-danger);
    }
  }

  .problemText {
    flex: 1 1 14rem;
  }

  .empty {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }

  .pendingRows {
    display: grid;
    gap: var(--space-4);
    padding-block: var(--space-2);
  }

  .pendingRow {
    display: grid;
    grid-template-columns: 4.5rem minmax(0, 12rem) minmax(0, 1fr);
    gap: var(--space-4);

    & > span {
      block-size: 1rem;
    }
  }

  .pendingDay {
    inline-size: 5rem;
    block-size: 0.875rem;
  }
}
